/**
 * Marke "Neu" an Bestellungen mit neuen Artikeln (Feature "Neue Artikel an Bestellungen markieren und
 * filtern"): ein Artikel ist neu, solange JTL fuer ihn keine Lagerbewegung kennt. Das Backend meldet das je
 * Position als `neuerArtikel` (`backend/src/jtl/neueArtikel.ts`), das Frontend reicht es durch
 * (`api/jtlBestellungen.ts#alsOrderItem`, `OrderItem.neuerArtikel`). Die Marke ist reine Anzeige - kein Status,
 * keine Spalte. Daneben der Schalter `NeuFilterSchalter` ("Neu (n)") fuer Board und Zeitstrahl; filtern und zaehlen
 * tun die Seiten selbst. NEUBAU ohne Bestandsvorbild; Styles in `styles/neubau/neuerArtikelMarke.css`.
 *
 * Die Helfer liegen hier und nicht in `lib/`: die portierten `lib/`-Module tragen Herkunftsmarken aus
 * `index.html`, Neucode ohne Vorbild gehoert nicht dorthin.
 */
import type { ReactNode } from "react";
import type { Order, OrderItem } from "../lib/types.ts";

/** So viele VERSCHIEDENE Nummern nennt der Tooltip hoechstens; bei mehr kuerzt er (`+ <N> weitere`). */
const TOOLTIP_NUMMERN_MAX = 5;

/** Die Positionen der Bestellung; alles, was keine Liste ist, zaehlt als keine Position. */
function positionenVon(order: Order): readonly (OrderItem | null | undefined)[] {
  return Array.isArray(order.items) ? (order.items as (OrderItem | null | undefined)[]) : [];
}

/** `true`, wenn mindestens eine Position `neuerArtikel === true` traegt (nie `!== false`: fehlend heisst nicht neu). */
export function hatNeueArtikel(order: Order): boolean {
  return positionenVon(order).some((position) => position?.neuerArtikel === true);
}

/**
 * Die VERSCHIEDENEN Artikelnummern (`sku`) der neuen Positionen in Positionsreihenfolge. Derselbe Artikel auf
 * zwei Positionen steht einmal da; leere oder fehlende Nummern entfallen.
 */
export function neueArtikelNummern(order: Order): string[] {
  const nummern = new Set<string>();
  for (const position of positionenVon(order)) {
    if (position?.neuerArtikel !== true) continue;
    const sku = position.sku;
    if (typeof sku === "string" && sku !== "") nummern.add(sku);
  }
  return [...nummern];
}

/**
 * Tooltip der Marke: eine Nummer im Singular, mehrere im Plural, ab sechs verschiedenen Nummern die ersten
 * fuenf und ` + <N> weitere`. Ohne Nummer (neue Position ohne Artikelnummer) nur der Grund.
 */
export function neuerArtikelTitel(nummern: readonly string[]): string {
  const grund = "in JTL noch keine Lagerbewegung";
  if (nummern.length === 0) return `Neuer Artikel – ${grund}`;
  const kopf = nummern.length === 1 ? "Neuer Artikel" : "Neue Artikel";
  const gezeigt = nummern.slice(0, TOOLTIP_NUMMERN_MAX).join(", ");
  const rest = nummern.length - TOOLTIP_NUMMERN_MAX;
  return `${kopf} – ${grund}: ${gezeigt}${rest > 0 ? ` + ${rest} weitere` : ""}`;
}

export function NeuerArtikelMarke({ order }: { order: Order }): ReactNode {
  if (!hatNeueArtikel(order)) return null;
  return (
    <span className="neuer-artikel-marke" title={neuerArtikelTitel(neueArtikelNummern(order))}>
      Neu
    </span>
  );
}

/**
 * Der Schalter "Neu (n)" in den Werkzeugleisten von Board (`Board/Werkzeugleiste.tsx`) und Zeitstrahl
 * (`TimelinePage/ZeitstrahlSeite.tsx`): eingeschaltet zeigt die Seite nur Bestellungen mit neuen Artikeln.
 * Eine Komponente fuer beide Seiten, damit Beschriftung, Tooltip und Zugaenglichkeit nicht auseinanderlaufen.
 *
 * KEIN `aria-label`: der zugaengliche Name ist der sichtbare Text `Neu (n)` (WCAG 2.5.3 "Label in Name"), die
 * Anzahl wird also vorgelesen; ein `aria-label` mit dem Handlungsnamen hat sie ueberdeckt. Der erklaerende
 * Satz steht nur im `title`.
 */
export function NeuFilterSchalter({
  an,
  anzahl,
  onUmschalten,
}: {
  an: boolean;
  anzahl: number;
  onUmschalten: (an: boolean) => void;
}): ReactNode {
  return (
    <button
      className="neu-filter-schalter"
      type="button"
      aria-pressed={an}
      onClick={() => onUmschalten(!an)}
      title="Nur Bestellungen mit neuen Artikeln zeigen"
    >
      {`Neu (${anzahl})`}
    </button>
  );
}
